import React, { memo, useState, useEffect } from 'react';
import { bool, func, number, string } from 'prop-types';
import AutoCompleteDropdown from '../../../global/atoms/autoComplete/AutoCompleteDropdown';
import { getLocations } from '../../../global/modules/location-autocomplete/api/getLocations';
import { handleAutoAddress } from './utils/addressUtils';
import { ENTER_KEY } from '../../../../constants/screenConstants';
import { DELAY_TIME } from '../../../global/utils/commonUtils';
import { COUNTRY } from '../../constants';
import './addressAutocomplete.scss';

const AddressAutocomplete = props => {
    const {
        buttonTestId,
        placeholderText,
        prefilledAddress,
        inputAriaLabel,
        handleLocationChange,
        selectedAddress,
        showAddressLineOneOnly,
        locationAutocompleteWrapper,
        source,
        clearField,
        customCountryCode,
        isLocationAvailable,
        errorFromLocationAutocomplete,
        onSelectAddress,
        isManualAddress,
        listItemTestId,
        maxLength,
        inputId,
        setAddressManually,
        onInputFocus,
        isError
    } = props;

    const [autoSuggestionList, setAutoSuggestionList] = useState([]);
    const [selectedPlaceId, setSelectedPlaceId] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [autocompleteText, setAutocompleteText] = useState(prefilledAddress || '');
    let debouncedFetchLocation = null;

    useEffect(() => {
        setAutocompleteText('');
        setAutoSuggestionList([]);
    }, [clearField]);

    useEffect(() => {
        if (autocompleteText.length > 1 && (isManualAddress || autocompleteText !== prefilledAddress)) {
            if (autocompleteText.length === 2) {
                fetchLocationAPI();
            } else {
                debouncedFetchLocation = setTimeout(() => {
                    fetchLocationAPI();
                }, DELAY_TIME);
                return () => {
                    clearTimeout(debouncedFetchLocation);
                };
            }
        } else if (autocompleteText.length === 0) {
            errorFromLocationAutocomplete && errorFromLocationAutocomplete('Error in address.');
        }
    }, [autocompleteText]);

    useEffect(() => {
        setAutocompleteText(prefilledAddress);
    }, [prefilledAddress]);

    useEffect(() => {
        if (selectedPlaceId !== '') {
            onSelectAddress && onSelectAddress(selectedPlaceId);
        }
    }, [selectedPlaceId]);

    const fetchLocationAPI = async () => {
        const isUS = customCountryCode ? customCountryCode === COUNTRY.US : true;
        const response = await getLocations(autocompleteText, customCountryCode, isLocationAvailable, isUS, {}, true);
        if (!response.error) {
            const res = response?.data;
            let computedSuggestionsList = res?.predictions;
            setAutoSuggestionList(computedSuggestionsList);
            errorFromLocationAutocomplete && errorFromLocationAutocomplete(false);
        } else {
            errorFromLocationAutocomplete && errorFromLocationAutocomplete('Error in address');
        }
    };

    const onChangeInput = e => {
        const addInputValue = e?.target?.value;
        if (addInputValue?.length <= maxLength) {
            setSelectedPlaceId('');
            if (e.target.value.length > 1) {
                setIsOpen(true);
            } else {
                setIsOpen(false);
            }
            errorFromLocationAutocomplete && errorFromLocationAutocomplete('');
            setAutocompleteText(addInputValue);
            isManualAddress && setAddressManually && setAddressManually(addInputValue);
        }
    };

    const setOpenDropDown = () => {
        setIsOpen(false);
    };

    const onPressAutoCompleteList = async (e, val, placeId) => {
        setIsOpen(false);
        selectedAddress && selectedAddress(val);
        if (showAddressLineOneOnly) {
            setAutocompleteText(val?.split(',')[0]);
        } else {
            setAutocompleteText(val);
        }
        const countryId = customCountryCode === COUNTRY.US ? 1 : 2;
        handleLocationChange && handleLocationChange(await handleAutoAddress(placeId, val, countryId));
        setSelectedPlaceId(placeId);
    };

    const onEnterKeyPressed = (e, item) => {
        if (e.key === ENTER_KEY) {
            onPressAutoCompleteList(e, item?.structuredFormatting?.mainText, item?.placeId);
        }
    };

    //function to switch between suggested addresses on up/down arrow key press
    const handleKeyboardNavigation = (e, placeId) => {
        const listItems = document.querySelectorAll('.listItems') || [];
        if (e.keyCode === 40 && e.target.tagName.toLowerCase() === 'input') {
            listItems[0]?.focus();
        }
        if (e.keyCode === 38 || e.keyCode === 40) {
            const currentFocusedAddress = autoSuggestionList.filter(item => item.placeId === placeId)[0];
            let indexOfCurrentFocusedAddress = autoSuggestionList.indexOf(currentFocusedAddress);
            if (
                (indexOfCurrentFocusedAddress === 0 && e.keyCode === 38) ||
                (indexOfCurrentFocusedAddress === autoSuggestionList.length - 1 && e.keyCode === 40)
            ) {
                //continue
            } else {
                if (e.keyCode === 38) {
                    document.getElementById(autoSuggestionList[indexOfCurrentFocusedAddress - 1].placeId).focus();
                } else {
                    document.getElementById(autoSuggestionList[indexOfCurrentFocusedAddress + 1].placeId).focus();
                }
            }
        }
    };

    const renderSuggestion = (item, index) => {
        return (
            isOpen && (
                <div
                    key={item?.placeId}
                    tabIndex={0}
                    className="listItems"
                    onClick={e => onPressAutoCompleteList(e, item?.structuredFormatting?.mainText, item?.placeId)}
                    onKeyPress={e => onEnterKeyPressed(e, item)}
                    data-testid={listItemTestId}
                    onKeyDown={e => handleKeyboardNavigation(e, item?.placeId)}>
                    <li
                        tabIndex={-1}
                        key={item?.placeId}
                        id={item?.placeId}
                        onKeyPress={e => onEnterKeyPressed(e, item)}
                        data-testid={buttonTestId}
                        role="option"
                        aria-label={`${inputAriaLabel} ${item?.structuredFormatting?.mainText}`}>
                        {item?.description}
                    </li>
                </div>
            )
        );
    };

    return (
        <>
            <AutoCompleteDropdown
                inputId={inputId}
                placeholderText={placeholderText}
                onChange={onChangeInput}
                onSelect={setOpenDropDown}
                isDropDownOpen={isOpen}
                renderSuggestion={renderSuggestion}
                suggestions={autoSuggestionList}
                value={autocompleteText}
                autoCompleteWrapperClass={`location-autocomplete ${locationAutocompleteWrapper}`}
                autoCompleteInputClass={'cmp-Field__field__input'}
                source={source}
                inputAriaLabel={inputAriaLabel}
                onInputFocus={onInputFocus}
                name={name}
                isError={isError}
            />
        </>
    );
};

AddressAutocomplete.propTypes = {
    maxLength: number,
    source: string,
    inputId: string,
    buttonTestId: string,
    listItemTestId: string,
    inputAriaLabel: string,
    placeholderText: string,
    prefilledAddress: string,
    customCountryCode: string,
    locationAutocompleteWrapper: string,
    clearField: bool,
    isManualAddress: bool,
    isLocationAvailable: bool,
    showAddressLineOneOnly: bool,
    setAddressManually: func,
    handleLocationChange: func,
    errorFromLocationAutocomplete: func,
    onInputFocus: func,
    name: string,
    isError: bool
};

AddressAutocomplete.defaultProps = {
    maxLength: 100,
    source: '',
    inputId: '',
    buttonTestId: '',
    inputAriaLabel: '',
    listItemTestId: '',
    placeholderText: '',
    prefilledAddress: '',
    customCountryCode: 'US',
    locationAutocompleteWrapper: '',
    clearField: false,
    isLocationAvailable: true,
    showAddressLineOneOnly: false,
    setAddressManually: () => { },
    handleLocationChange: () => { },
    errorFromLocationAutocomplete: () => { },
    onInputFocus: () => { },
    name: '',
    isError: false
};

export default memo(AddressAutocomplete);
